import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Badge } from '@/shared/ui/badge'
import { Checkbox } from '@/shared/ui/checkbox'
import { Label } from '@/shared/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { useCan } from '@/shared/hooks/useCan'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { adminLoggersPath, editAdminLoggerPath, newAdminLoggerPath } from '@/shared/lib/routes'
import type { WebAdminLogger } from '@/shared/types/web'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import UsersLoggersTabs from '@/pages/Admin/UsersLoggersTabs'

interface Filters {
  search: string
  onlyActive: boolean
}

interface Props {
  loggers: WebAdminLogger[]
  pagination: PaginationMeta
  filters: Filters
  currentUserIsAdmin: boolean
  [key: string]: unknown
}

const LoggerManagementPage = () => {
  const { loggers, pagination, filters, currentUserIsAdmin } = usePage<Props>().props
  const canCreateLogger = useCan(Resources.ADMIN_LOGGER, Actions.CREATE)
  const canUpdateLogger = useCan(Resources.ADMIN_LOGGER, Actions.UPDATE)

  useBreadcrumbs([{ label: t('nav.administration') }, { label: t('nav.loggers') }])

  const [search, setSearch] = useState(filters.search)
  const [onlyActive, setOnlyActive] = useState(filters.onlyActive)

  const navigate = (overrides: { search?: string; only_active?: boolean } = {}) => {
    const effectiveSearch = overrides.search ?? search
    const effectiveOnlyActive = overrides.only_active ?? onlyActive
    const params: Record<string, string> = {}
    if (effectiveSearch) params.search = effectiveSearch
    if (effectiveOnlyActive) params.only_active = 'true'
    router.visit(adminLoggersPath(Object.keys(params).length ? params : {}), {
      preserveState: false,
    })
  }

  const debouncedNavigate = useDebouncedCallback((value: string) => {
    navigate({ search: value })
  }, 400)

  const activeFilterCount = [search, onlyActive].filter(Boolean).length

  const clearFilters = () => {
    debouncedNavigate.cancel()
    setSearch('')
    setOnlyActive(false)
    router.visit(adminLoggersPath(), { preserveState: false })
  }

  const goToPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(page))
    router.visit(`${adminLoggersPath()}?${params}`, { preserveState: true })
  }

  return (
    <div className="space-y-6">
      <UsersLoggersTabs />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('admin.loggers.pageTitle')}</h1>
        {currentUserIsAdmin && canCreateLogger && (
          <Button asChild>
            <Link href={newAdminLoggerPath()}>{t('admin.loggers.newButton')}</Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="space-y-3 rounded-md border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder={t('admin.loggers.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debouncedNavigate(e.target.value)
            }}
            className="w-72"
          />

          <div className="flex items-center gap-2">
            <Checkbox
              id="only-active"
              checked={onlyActive}
              onCheckedChange={(checked) => {
                const value = Boolean(checked)
                setOnlyActive(value)
                navigate({ only_active: value })
              }}
            />
            <Label htmlFor="only-active" className="cursor-pointer text-sm font-normal">
              {t('admin.loggers.onlyActiveLabel')}
            </Label>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 text-muted-foreground"
                onClick={clearFilters}
              >
                {t('admin.loggers.clearFilters')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {loggers.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('admin.loggers.table.empty')}</p>
          </div>
        ) : (
          loggers.map((logger) => (
            <div key={logger.id} className="rounded-lg border bg-card p-3 shadow-sm space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{logger.displayName}</p>
                  {logger.jobTitle && (
                    <p className="text-xs text-muted-foreground">{logger.jobTitle}</p>
                  )}
                  {logger.employeeId && (
                    <p className="text-xs text-muted-foreground">{logger.employeeId}</p>
                  )}
                </div>
                {logger.active ? (
                  <Badge variant="outline" className="shrink-0 text-green-700 border-green-300">
                    {t('admin.loggers.table.active')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="shrink-0 text-red-700 border-red-300">
                    {t('admin.loggers.table.inactive')}
                  </Badge>
                )}
              </div>
              {currentUserIsAdmin && canUpdateLogger && (
                <div className="flex justify-end">
                  <Button asChild variant="outline" size="sm">
                    <Link href={editAdminLoggerPath(logger.id)}>
                      {t('admin.loggers.editButton')}
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.loggers.table.name')}</TableHead>
              <TableHead>{t('admin.loggers.table.jobTitle')}</TableHead>
              <TableHead>{t('admin.loggers.table.employeeId')}</TableHead>
              <TableHead>{t('admin.loggers.table.status')}</TableHead>
              {currentUserIsAdmin && canUpdateLogger && (
                <TableHead className="w-[1%] text-right">
                  {t('admin.loggers.table.actions')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loggers.map((logger) => (
              <TableRow key={logger.id}>
                <TableCell className="font-medium">{logger.displayName}</TableCell>
                <TableCell>{logger.jobTitle ?? '—'}</TableCell>
                <TableCell>{logger.employeeId ?? '—'}</TableCell>
                <TableCell>
                  {logger.active ? (
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      {t('admin.loggers.table.active')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-red-700 border-red-300">
                      {t('admin.loggers.table.inactive')}
                    </Badge>
                  )}
                </TableCell>
                {currentUserIsAdmin && canUpdateLogger && (
                  <TableCell className="text-right">
                    <Button asChild variant="outline" size="sm">
                      <Link href={editAdminLoggerPath(logger.id)}>
                        {t('admin.loggers.editButton')}
                      </Link>
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {loggers.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={currentUserIsAdmin && canUpdateLogger ? 5 : 4}
                  className="text-center text-muted-foreground py-8"
                >
                  {t('admin.loggers.table.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {t('admin.users.paginationPage')} {pagination.page} {t('admin.users.paginationOf')}{' '}
          {pagination.totalPages} ({pagination.total} {t('admin.loggers.paginationNoun')})
        </span>
        {pagination.totalPages > 1 && (
          <div className="flex gap-2">
            {pagination.page > 1 && (
              <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page - 1)}>
                {t('admin.users.previous')}
              </Button>
            )}
            {pagination.page < pagination.totalPages && (
              <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page + 1)}>
                {t('admin.users.next')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default LoggerManagementPage
